import { cva, type VariantProps } from 'class-variance-authority';

/**
 * ChoiceInput visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, frames `3420:8272` (light) / `4225:802` (dark)
 * ("Choice input"). Reference cells (light frame, Inline / S / Checkbox):
 *   - 3420:8259 – Default   (glass fill, secondary-300 border)
 *   - 3486:2804 – Hover     (border deepens to secondary-400)
 *   - 3490:1516 – Pressed   (solid secondary-50 fill + secondary-400 border)
 *   - 3420:8261 – Focus     (1px secondary-600 ring, 1px outside the edge)
 *   - 3420:8253 – Disabled  (opacity-50)
 *   - 4640:17651 – Selected, variant secondary ("Selected state -Secondary")
 *   - 4640:16090 – Selected, variant primary   ("Selected -Primary accent")
 * Slot references: 3420:8265 (icon S) · 3420:8166 (image L) · 3420:8204
 * (description L) · 3420:8215 (description + icon L) · 3423:8349
 * (description + image L).
 *
 * Anatomy: one bordered `Pressable` row — optional left slot (icon or image
 * thumbnail) · flexible label column (label, or title + description) · a
 * presentational Checkbox / Radio in the right slot. The row owns the
 * interaction; the embedded control is `interactive={false}`.
 *
 * Axes (named after Figma's component properties):
 *   size:            s (36px) · m (40px) · l (44px) · xl (48px) · 2xl (52px)
 *                    — inline row heights; radius 16/18/18/20/20.
 *   withDescription: Figma "Type" (Inline ↔ With Description). Description
 *                    exists only at l / xl / 2xl; the row hugs its two text
 *                    lines instead of the fixed inline height.
 *   leftSlot:        Figma "Left slot instance" (none · icon · image). Image
 *                    exists only at l / xl / 2xl and hugs the 42/42/52px
 *                    thumbnail.
 *   variant:         primary · secondary — which SELECTED treatment the row
 *                    gets (unselected rows are identical in both). Selected
 *                    secondary (4640:17651) keeps the Default row styling and
 *                    just ticks the default-type control. Selected primary
 *                    (4640:16090) re-tones the whole row to the brand accent:
 *                    solid primary-50 fill (no glass) + primary-400 border,
 *                    all text and the left icon primary-950, and the primary-
 *                    type Checkbox. Hover/press keep the accent colors — Figma
 *                    defines no hover-on-selected cell, so the selected colors
 *                    simply override the neutral ramp.
 *   selected:        the checked axis (surfaced as `checked`).
 *
 * Colors (light, via `get_variable_defs`):
 *   fill       brand/secondary/30 = rgba(255,255,255,.7) → `secondary-50/70`
 *              glass (auto-inverts to translucent black) over a Figma "Glass"
 *              effect (radius 4) → web-only backdrop blur, like the Checkbox.
 *   border     secondary-300 → hover secondary-400
 *   pressed    solid secondary-50 fill + secondary-400 border. (Figma's
 *              secondary-50 is #fafafa; our scale maps it to #ffffff — the
 *              same imperceptible 5/255 lift the Chip documents.)
 *   focus      1px secondary-600 ring with a 1px transparent gap (Figma draws
 *              it as an outer wrapper with `border + p-px`, radius + 1).
 *   label      secondary-600 · title secondary-800 · description secondary-500
 *
 * Disabled: `opacity-50` (Figma disabled cells) — supplied by the Pressable
 * primitive on both platforms, so no CVA override is needed.
 *
 * Dark mode is automatic: every token resolves through the auto-inverting
 * CSS-var palette (dark frame `4225:802` uses the SAME var names with the
 * mirrored values — 300→#404040, 600→#a1a1a1, glass →rgba(0,0,0,.7)), so NO
 * `dark:` classes are needed. The lone exception is the left-slot *icon*
 * color (a hex prop) — see `ChoiceInput.icons.ts`.
 */
export const choiceInputVariants = cva(
  'relative flex w-full flex-row items-center overflow-hidden border ' +
    'bg-secondary-50/70 web:backdrop-blur-[2px] border-secondary-300 ' +
    'enabled:hover:border-secondary-400 ' +
    'enabled:active:bg-secondary-50 enabled:active:border-secondary-400 ' +
    'native:active:bg-secondary-50 native:active:border-secondary-400 ' +
    'web:transition-colors web:duration-150 web:ease-out ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-1 web:focus-visible:ring-secondary-600',
  {
    variants: {
      // Inline geometry per size (heights 36→52, radius 16/18/18/20/20).
      // Right padding is per-size (10/12/12/14/14px); left is a constant 12px.
      size: {
        s: 'h-9 gap-2 rounded-2xl pl-3 pr-2.5',
        m: 'h-10 gap-2 rounded-[18px] px-3',
        l: 'h-11 gap-2 rounded-[18px] px-3',
        xl: 'h-12 gap-2 rounded-[20px] pl-3 pr-3.5',
        '2xl': 'h-[52px] gap-2 rounded-[20px] pl-3 pr-3.5',
      },
      leftSlot: { none: '', icon: '', image: '' },
      withDescription: { true: '', false: '' },
      variant: { primary: '', secondary: '' },
      selected: { true: '', false: '' },
    },
    compoundVariants: [
      // ── Selected / primary variant — the brand-accent row (4640:16090):
      // solid primary-50 fill (glass off) + primary-400 border. The accent
      // also overrides the neutral hover/press ramp so interacting with a
      // selected row never flips it back to gray.
      {
        variant: 'primary',
        selected: true,
        class:
          'bg-primary-50 web:backdrop-blur-none border-primary-400 ' +
          'enabled:hover:border-primary-400 ' +
          'enabled:active:bg-primary-50 enabled:active:border-primary-400 ' +
          'native:active:bg-primary-50 native:active:border-primary-400',
      },
      // Selected / secondary variant (4640:17651) = the Default row styling —
      // only the embedded control changes, so no row classes.

      // ── With Description: the row hugs its two text lines. Vertical padding
      // steps 10/12/16px per Figma (heights 62/74/84).
      { withDescription: true, size: 'l', class: 'h-auto px-3 py-2.5' },
      { withDescription: true, size: 'xl', class: 'h-auto p-3' },
      { withDescription: true, size: '2xl', class: 'h-auto px-3 py-4' },

      // ── Icon slot. Inline rows keep a tight 10px right padding at every
      // size (Figma), and the icon↔text gap widens at xl/2xl.
      { leftSlot: 'icon', withDescription: false, class: 'pr-2.5' },
      { leftSlot: 'icon', withDescription: false, size: 'xl', class: 'gap-2.5' },
      { leftSlot: 'icon', withDescription: false, size: '2xl', class: 'gap-3' },
      // Description rows keep their 12px padding but widen the gap.
      { leftSlot: 'icon', withDescription: true, class: 'gap-3' },

      // ── Image slot (l/xl/2xl only): the row hugs the 42/42/52px thumbnail
      // (py 8/10/12px), right padding widens to 16px, gap to 12px. Listed
      // after the description compounds so these paddings win for
      // "With Description + image" (Figma 3423:8349 = image geometry).
      { leftSlot: 'image', size: 'l', class: 'h-auto gap-3 py-2 pl-3 pr-4' },
      { leftSlot: 'image', size: 'xl', class: 'h-auto gap-3 py-2.5 pl-3 pr-4' },
      { leftSlot: 'image', size: '2xl', class: 'h-auto gap-3 py-3 pl-3 pr-4' },
    ],
    defaultVariants: {
      size: 'm',
      leftSlot: 'none',
      withDescription: false,
      variant: 'primary',
      selected: false,
    },
  },
);

/**
 * The single-line label of Inline rows without an image: B2 (14/22) at s/m,
 * B1 (16/24) at l/xl/2xl, Medium, secondary-600. Rows with a description or
 * an image use {@link choiceInputTitleVariants} for the first line instead.
 */
export const choiceInputLabelVariants = cva(
  'text-secondary-600 font-medium native:font-mona-medium',
  {
    variants: {
      size: {
        s: 'text-b2',
        m: 'text-b2',
        l: 'text-b1',
        xl: 'text-b1',
        '2xl': 'text-b1',
      },
    },
    defaultVariants: { size: 'm' },
  },
);

/**
 * The title line — used by "With Description" rows AND by inline rows with an
 * image thumbnail (Figma styles both labels identically): L1 (Bold 16/20) at
 * l, H3 (SemiBold 18/26) at xl, H2 (SemiBold 20/28) at 2xl, secondary-800.
 * Only l/xl/2xl exist in Figma; s/m alias the l style defensively.
 */
export const choiceInputTitleVariants = cva('text-secondary-800', {
  variants: {
    size: {
      s: 'text-l1 font-bold native:font-mona-bold',
      m: 'text-l1 font-bold native:font-mona-bold',
      l: 'text-l1 font-bold native:font-mona-bold',
      xl: 'text-h3 font-semibold native:font-mona-semibold',
      '2xl': 'text-h2 font-semibold native:font-mona-semibold',
    },
  },
  defaultVariants: { size: 'l' },
});

/**
 * The description line: B2 (14/22) at l, B1 (16/24) at xl/2xl, Medium,
 * secondary-500.
 */
export const choiceInputDescriptionVariants = cva(
  'text-fg-subtle font-medium native:font-mona-medium',
  {
    variants: {
      size: {
        s: 'text-b2',
        m: 'text-b2',
        l: 'text-b2',
        xl: 'text-b1',
        '2xl': 'text-b1',
      },
    },
    defaultVariants: { size: 'l' },
  },
);

/**
 * The image-thumbnail wrapper: 42px at l/xl, 52px at 2xl, 8px radius (Figma's
 * 7.875px is a component-scaling artifact), clipped.
 */
export const choiceInputImageVariants = cva('shrink-0 overflow-hidden rounded-lg', {
  variants: {
    size: {
      s: 'h-[42px] w-[42px]',
      m: 'h-[42px] w-[42px]',
      l: 'h-[42px] w-[42px]',
      xl: 'h-[42px] w-[42px]',
      '2xl': 'h-[52px] w-[52px]',
    },
  },
  defaultVariants: { size: 'l' },
});

export type ChoiceInputVariantProps = VariantProps<typeof choiceInputVariants>;
